import { type VariantProps, cva } from 'class-variance-authority';
import type { PrimitiveProps } from 'radix-vue';
import type { HTMLAttributes } from 'vue';
import type { SkeletonVariants } from '../skeleton';

export { default as Button } from './Button.vue';

export type ButtonProps = PrimitiveProps & {
	variant?: ButtonVariants['variant'];
	size?: ButtonVariants['size'];
	class?: HTMLAttributes['class'];
	disabled?: boolean;
	isSkeletonLoading?: boolean;
	skeletonClasses?: string;
	skeletonVariant?: SkeletonVariants['variant'];
};

export const buttonVariants = cva(
	'group/button flex size-full cursor-pointer items-center justify-center whitespace-nowrap border font-medium transition-colors duration-300 focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:border-white-dark-3 disabled:bg-white-dark-3 disabled:text-primary-light-1',
	{
		variants: {
			variant: {
				primary:
					'border-primary bg-primary text-white hover:border-primary-light-4 hover:bg-primary-light-4 focus:border-primary-light-4 focus:bg-primary-light-4',
				secondary:
					'border-white-dark-4 bg-white text-primary hover:border-primary focus:border-primary',
				tertiary:
					'border-secondary bg-secondary text-primary hover:border-secondary-dark-1 hover:bg-secondary-dark-1 focus:border-secondary-dark-1 focus:bg-secondary-dark-1',
				'primary-dark':
					'border-white bg-white text-primary hover:border-white-dark-3 hover:bg-white-dark-3 focus:border-white-dark-3 focus:bg-white-dark-3',
				'secondary-dark':
					'border-primary-light-3 bg-primary text-white hover:border-white focus:border-white',
				transparent: 'border-transparent bg-transparent text-primary',
				beige:
					'border-beige bg-beige text-primary hover:border-white focus:border-white',
				'beige-outline':
					'border-beige bg-primary text-white hover:border-white focus:border-white',
				'beige-outline-transparent':
					'border-beige bg-transparent text-white hover:border-white focus:border-white',
				'primary-light-4':
					'border border-primary-light-4 bg-primary-light-4 text-white hover:bg-primary-light-3',
				white:
					'border-white bg-white text-primary hover:border-white focus:border-white-dark-3'
			},
			size: {
				xs: 'gap-1 px-3 py-2 text-xs leading-5', // 12px 8px
				sm: 'gap-2 px-4 py-2 text-sm leading-5', // 16px 8px
				md: 'gap-3 px-5 py-3 text-base leading-[26px]', // 20px 12px
				lg: 'gap-4 px-6 py-3 text-lg leading-[150%]' // 24px 12px
			}
		},
		defaultVariants: {
			variant: 'primary',
			size: 'md'
		}
	}
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
